import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ReactTooltip from "react-tooltip";
import { useTracking } from "react-tracking";
import { closeViewModalAction } from "../../app/actions/viewModalActions";
import { technologyCleanSaved } from "../../app/actions/technologyActions";
import { alternativeCleanSaved } from "../../app/actions/alternativeActions";
import { experienceCleanSaved } from "../../app/actions/experienceActions";
import { innovationCleanSaved } from "../../app/actions/innovationActions";
import { assistanceCleanSaved } from "../../app/actions/assistanceActions";
import { financialCleanSaved } from "../../app/actions/financialActions";
import { visionCleanSaved } from "../../app/actions/visionActions";
import { regulationCleanSaved } from "../../app/actions/regulationActions";
import useActiveSpecialistRole from "../../app/hooks/useActiveSpecialistRole";
import {
  MODULE_FICHA_NAV,
  clearFichaReturnContext,
  persistFichaReturnContext,
  readFichaReturnContext,
  resolveFichaReturnPath
} from "../../app/helpers/moduleFichaNavHelper";
import DisplayNameAndRole from "./DisplayNameAndRole";

const CLEAN_SAVED = {
  technology: technologyCleanSaved,
  alternative: alternativeCleanSaved,
  experience: experienceCleanSaved,
  innovation: innovationCleanSaved,
  assistance: assistanceCleanSaved,
  financial: financialCleanSaved,
  vision: visionCleanSaved,
  regulation: regulationCleanSaved
};

const NavbarIconsModuleContext = ({
  moduleProcess,
  mode = "ficha",
  returnTo: returnToProp,
  returnLabelKey: returnLabelKeyProp,
  returnLabel: returnLabelProp,
  createTo = "",
  createLabel = ""
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { trackEvent } = useTracking();
  const { t } = useTranslation();
  const { isUserAuthenticated } = useSelector((state) => state.auth);
  const { isSpecialistFeeder } = useSelector((state) => state.userRole.roleLogin);
  const { flowEngineEnabled, canCreate } = useActiveSpecialistRole();
  const canShowCreateButton = flowEngineEnabled ? canCreate : isSpecialistFeeder;
  const [activeIcon, setActiveIcon] = useState(null);

  const process = String(moduleProcess || "").toLowerCase();
  const nav = MODULE_FICHA_NAV[process] || {};
  const stored = readFichaReturnContext(process);
  const returnTo = returnToProp
    || resolveFichaReturnPath(process, location.state || {});
  const returnLabelKey = returnLabelKeyProp
    || location.state?.returnLabelKey
    || stored.returnLabelKey
    || (nav.i18nPrefix ? `${nav.i18nPrefix}.page.list` : "");
  const returnLabel = returnLabelProp
    || location.state?.returnLabel
    || stored.returnLabel
    || (returnLabelKey ? t(returnLabelKey) : t(`${nav.i18nPrefix}.page.list`));

  const showReturn = mode === "ficha";
  const createHref = createTo || nav.newPath;
  const showCreate = Boolean(createTo) || (canShowCreateButton && !!nav.newPath);

  useEffect(() => {
    if (location.state?.returnTo) {
      persistFichaReturnContext(process, location.state);
    } else if (returnToProp) {
      persistFichaReturnContext(process, {
        returnTo: returnToProp,
        returnLabelKey: returnLabelKeyProp,
        returnLabel: returnLabelProp
      });
    }
  }, [process, location.state, returnToProp, returnLabelKeyProp, returnLabelProp]);

  const iconActiveStyle = (icon) => (activeIcon === icon ? "icon-d-flex active" : "icon-d-flex");

  const createNavState = {
    returnTo: mode === "list" ? (nav.listPath || returnTo) : returnTo,
    returnLabelKey: mode === "list"
      ? (nav.i18nPrefix ? `${nav.i18nPrefix}.page.list` : returnLabelKey)
      : returnLabelKey,
    returnLabel: mode === "list" ? "" : returnLabel
  };

  const handleReturn = () => {
    trackEvent({ component: "NavbarIconsModuleContext", event: `${process}-Return-Clicked` });
    dispatch(closeViewModalAction());
    setActiveIcon("list");
    const target = returnTo;
    clearFichaReturnContext(process);
    navigate(target);
  };

  const handleNew = () => {
    trackEvent({ component: "NavbarIconsModuleContext", event: `${process}-New-Clicked` });
    const cleanSaved = !createTo ? CLEAN_SAVED[process] : null;
    if (cleanSaved) {
      dispatch(cleanSaved());
    }
    dispatch(closeViewModalAction());
    setActiveIcon("new");
    persistFichaReturnContext(process, createNavState);
  };

  const returnTipId = `moduleContextReturnTip-${process}`;
  const newTipId = `moduleContextNewTip-${process}`;

  return (
    <div className="d-flex justify-content-between align-items-center flex-nowrap gradient-custom-sky custom-nav-height">
      {isUserAuthenticated && (
        <div className="navbar-module-identity">
          <DisplayNameAndRole />
        </div>
      )}
      <div className="navbar-module-actions">
        {showReturn && (
          <button
            type="button"
            data-tip
            data-for={returnTipId}
            className="p-2 bd-highlight border-0 bg-transparent"
            onClick={handleReturn}
          >
            <i className={`fa fa-table fa-2x ${iconActiveStyle("list")}`} aria-hidden="true" />
          </button>
        )}
        {showCreate && (
          <Link
            data-tip
            data-for={newTipId}
            to={createHref}
            state={createNavState}
            className="p-2 bd-highlight"
            onClick={handleNew}
          >
            <i className={`fa fa-plus-square fa-2x ${iconActiveStyle("new")}`} aria-hidden="true" />
          </Link>
        )}
      </div>
      <ReactTooltip id={returnTipId} place="left" effect="solid">
        {returnLabel}
      </ReactTooltip>
      <ReactTooltip id={newTipId} place="left" effect="solid">
        {createLabel || (nav.i18nPrefix ? t(`${nav.i18nPrefix}.page.register`) : "")}
      </ReactTooltip>
    </div>
  );
};

export default NavbarIconsModuleContext;
